import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * SettingsLandingCard
 *
 * A navigable card used in the /settings landing page grid.
 *
 * Props:
 *   icon        – Lucide icon component
 *   title       – Card title
 *   description – Optional supporting text
 *   href        – Route to navigate to on click
 *   iconBg      – Tailwind gradient classes for the icon container (default: blue)
 */
const SettingsLandingCard = ({
  icon: Icon,
  title,
  description,
  href,
  iconBg = "from-gradient-primary to-gradient-accent",
}) => {
  const navigate = useNavigate();

  return (
    <motion.button
      type="button"
      onClick={() => navigate(href)}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.15 }}
      className="photon-settings-card group w-full text-left flex items-start gap-3.5 px-4 py-4 rounded-xl border border-edge-neutral-surface dark:border-edge-dialog-border bg-surface-card hover:border-edge-selection-border dark:hover:border-edge-muted-control hover:shadow-sm transition-all duration-150"
    >
      {/* Icon */}
      <div
        className={`photon-settings-icon flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-bl ${iconBg} flex items-center justify-center`}
      >
        <Icon className="w-4 h-4 text-ink-white" />
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink-ink dark:text-ink-white leading-5">
          {title}
        </p>
        {description && (
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5 leading-4">
            {description}
          </p>
        )}
      </div>

      {/* Arrow */}
      <ChevronRight className="w-4 h-4 text-ink-selection-border dark:text-ink-muted-control flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
    </motion.button>
  );
};

export default SettingsLandingCard;
